// Copyright (c) Docugami, Inc. All rights reserved.

import { createFolderId } from "components/file_management/file_helpers";
import { useEffect, useMemo, useState } from "react";
import { createSelectLabelByIdOrValue } from "store/dgml/dgmlSelectors";
import { selectAllLabels, useAddLabelMutation, useLabelChunksMutation, useRemoveLabelsMutation, useRenameLabelMutation } from "store/frontend/frontendSlice";
import { selectLabelState } from "store/frontend/selectors";
import { getValidatedDecodedTagErrorMessage, isValidDecodedTag } from "utils/TagStandardUtils";
import { useKeyPressHandler } from "./KeyPressHandler";
import { useAppSelector } from "./redux";

export interface ILabelText {
    isEditing: boolean;
    label: string;
    setLabel: (input: string) => void;
    isValid: boolean;
    saveLabel: () => Promise<string | undefined>;
    undo: () => void;
    onEdit: () => void;
    documentId: string;
    clusterId: string;
    hintLabels: string[];
    recentLabels: string[];
    errorMessage?: string;
}

const selectLabelByIdOrValue = createSelectLabelByIdOrValue();
export function useLabelText(clusterId: string, documentId: string, label: string, labelId?: string, targetChunks?: string[], modifyLabel: boolean = true): ILabelText {
    const [labelValue, setLabelValue] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [initialValue, setInitialValue] = useState('');

    const recentLabels = useAppSelector(state => state.sdv.recentLabels?.[clusterId] ?? []);
    const { data: labelState } = useAppSelector(selectLabelState(clusterId));
    const labelSchema = useAppSelector(state => selectLabelByIdOrValue(state, clusterId, labelId ?? label));

    const [triggerAddLabel] = useAddLabelMutation();
    const [triggerLabelChunks] = useLabelChunksMutation();
    const [triggerModifyLabel] = useRenameLabelMutation();
    const [triggerRemoveLabels] = useRemoveLabelsMutation();

    useKeyPressHandler(isEditing ? 'Escape' : undefined, () => {
        undo();
    });

    useEffect(() => {
        setLabelValue(label ?? '');
        setIsEditing(false);
    }, [label])

    const isValid: boolean = useMemo(() => {
        const targetLabel = labelValue?.trim();
        const isValidEmpty = !modifyLabel || !!targetLabel;
        return isValidEmpty && isValidDecodedTag(targetLabel);
    }, [labelValue, modifyLabel]);

    function onEdit() {
        setInitialValue(labelValue);
        setIsEditing(true);
    }

    function undo() {
        setIsEditing(false);
        setLabelValue(initialValue);
        setInitialValue('');
    }

    async function accept() {
        if (!isValid) {
            throw new Error(`Invalid label: ${labelValue}`);
        }

        const targetLabel = labelValue.trim();
        if (!targetLabel) {
            if (!!targetChunks?.length) {
                await triggerRemoveLabels({
                    docsetId: clusterId,
                    fileId: createFolderId(documentId).fileId,
                    spanIds: targetChunks,
                    label: label,
                    labelId: labelSchema?.id
                })
            }
        }
        else {
            const labelHasChanged = targetLabel !== label;
            if (!!labelSchema && labelHasChanged && modifyLabel) {
                // There is an existing schema label, update the label value
                await triggerModifyLabel({
                    docsetId: clusterId,
                    label: targetLabel,
                    labelId: labelSchema?.id
                })
            }

            if (!!targetChunks?.length) {
                const targetLabelId = !!labelState ? selectAllLabels(labelState).find(x => x.label === targetLabel)?.id : undefined;
                triggerLabelChunks({
                    docsetId: clusterId,
                    fileId: createFolderId(documentId).fileId,
                    spanIds: targetChunks,
                    label: targetLabel,
                    labelId: targetLabelId
                })
            }
            // Handle creating a new label if there are no chunks being labelled
            else if (!labelSchema) {
                triggerAddLabel({
                    docsetId: clusterId,
                    label: targetLabel
                })
            }
        }

        setIsEditing(false);
        return targetLabel;
    }

    const hints = useMemo(() =>
        !!labelState ? selectAllLabels(labelState).filter(x => x.label !== labelValue).map(x => x.label) : []
        , [labelState, labelValue]);

    const errorMessage = useMemo(() => {
        if (!isValid) {
            return !labelValue ? "Label cannot be empty" : getValidatedDecodedTagErrorMessage(labelValue.trim());
        }
    }, [isValid, labelValue]);

    return {
        isEditing: isEditing,
        label: labelValue,
        setLabel: setLabelValue,
        isValid: isValid,
        onEdit: onEdit,
        saveLabel: accept,
        undo: undo,
        documentId: documentId,
        clusterId: clusterId,
        hintLabels: hints,
        recentLabels: recentLabels,
        errorMessage: errorMessage
    }
}